import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiGet } from '../api/client';
import { formatMinor, fetchWalletSnapshot } from '../api/payments';
import type { Game } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { GameGrid } from '../components/GameGrid';
import { SearchBar } from '../components/SearchBar';
import { aznMinorToPlayMinor, wageringPercent } from '../lib/fxDisplay';
import { fetchPlayOptions, type PlayCurrency } from '../api/preferences';

interface BonusGamesResponse {
  enabled: boolean;
  active_bonus: boolean;
  message: string | null;
  bonus_balance_minor: number;
  wagering: {
    multiplier?: number;
    bonus_balance_minor?: number;
    wagering_target_minor?: number;
    wagering_completed_minor?: number;
    wagering_remaining_minor?: number;
    status?: string;
    grants?: Array<{ expires_at?: string | null }>;
  } | null;
  games: Game[];
  total: number;
  target: number;
}

function expiryLabel(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale);
}

export function BonusGamesPage() {
  const { t, i18n } = useTranslation(['games', 'payments', 'common', 'wheel']);
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<BonusGamesResponse | null>(null);
  const [playCurrency, setPlayCurrency] = useState<PlayCurrency>('AZN');
  const [rate, setRate] = useState<string>('1');

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setPayload(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.all([
      apiGet<BonusGamesResponse>(`/api/v1/bonus-games${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ''}`),
      fetchPlayOptions().catch(() => null),
      fetchWalletSnapshot().catch(() => null),
    ])
      .then(([games, opts, wallet]) => {
        if (cancelled) return;
        setPayload(games);
        const play = (wallet?.play_currency || opts?.preferences?.play_currency || 'AZN') as PlayCurrency;
        setPlayCurrency(play);
        setRate(opts?.fx?.rates_azn_per_unit?.[play] || (play === 'AZN' ? '1' : '1'));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, query]);

  const locale = i18n.language || 'az';
  const toPlay = (azn: number) => aznMinorToPlayMinor(azn, playCurrency === 'AZN' ? 1 : rate);
  const bonusPlay = payload ? toPlay(payload.bonus_balance_minor || 0) : 0;
  const wager = payload?.wagering;
  const pct = wageringPercent(Number(wager?.wagering_completed_minor || 0), Number(wager?.wagering_target_minor || 0));
  const expiresAt = useMemo(() => {
    const grants = wager?.grants || [];
    const first = grants.find((g) => g.expires_at)?.expires_at || null;
    return first;
  }, [wager]);

  if (!user) {
    return (
      <section className="page-stack">
        <h1>🎁 {t('games:bonusGamesTitle', { defaultValue: 'Bonus Oyunları' })}</h1>
        <p className="muted">{t('common:loginRequired', { defaultValue: 'Davam etmək üçün daxil olun.' })}</p>
        <Link className="btn btn-primary" to="/auth/login">
          {t('common:login', { defaultValue: 'Daxil ol' })}
        </Link>
      </section>
    );
  }

  return (
    <section className="page-stack bonus-games-page">
      <header className="section-head">
        <h1>🎁 {t('games:bonusGamesTitle', { defaultValue: 'Bonus Oyunları' })}</h1>
        <p className="muted">
          {t('games:bonusGamesSubtitle', {
            defaultValue: 'Yalnız bonus balansı ilə — 150 uyğun slot',
          })}
        </p>
      </header>

      <div className="bonus-games-status">
        <div>
          <span className="muted">{t('payments:bonusAccount')}</span>
          <strong>{formatMinor(bonusPlay, playCurrency, locale)}</strong>
        </div>
        {wager && Number(wager.wagering_target_minor || 0) > 0 ? (
          <div className="balance-bonus-progress" aria-label={`${pct}%`}>
            <div className="balance-bonus-progress-track">
              <div className="balance-bonus-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="balance-bonus-progress-bounds">
              <span>
                {t('payments:bonusFrom', {
                  defaultValue: 'from {{amount}}',
                  amount: formatMinor(toPlay(Number(wager.wagering_completed_minor || 0)), playCurrency, locale),
                })}
              </span>
              <span>
                {t('payments:bonusTo', {
                  defaultValue: 'to {{amount}}',
                  amount: formatMinor(toPlay(Number(wager.wagering_target_minor || 0)), playCurrency, locale),
                })}
              </span>
            </div>
          </div>
        ) : null}
        <p className="muted">
          {t('games:bonusExpires', { defaultValue: 'Bitmə' })}: {expiryLabel(expiresAt, locale)}
          {wager?.multiplier ? ` · ${wager.multiplier}x` : ' · 30x'}
        </p>
      </div>

      {!payload?.active_bonus && !loading ? (
        <p className="form-error" role="status">
          {t('payments:noActiveBonus', { defaultValue: 'Hazırda aktiv bonusunuz yoxdur' })}
        </p>
      ) : (
        <>
          <SearchBar value={query} onChange={setQuery} />
          <GameGrid
            games={(payload?.games || []).map((g) => ({ ...g, id: g.id }))}
            loading={loading}
            error={error}
            emptyMessage={t('games:bonusGamesEmpty', { defaultValue: 'Bonus oyunu tapılmadı' })}
            fundingSource="bonus"
          />
        </>
      )}
    </section>
  );
}
